import React, { useEffect, useReducer, useCallback, useRef } from "react";
import Context from "./Context";
import { auth as firebaseAuth } from "firebaseClient";
import { onIdTokenChanged } from "firebase/auth";
import { membershipTypes, roles } from "../../config/permissionDefs";
import { ONBOARDING_STEPS } from "types/onboarding";
import { AuthStatus } from "types/auth";
import {
  resolveTenantFromClaims,
  saveLastTenant,
} from "providers/Auth/helpers";

export { getLastTenantKey } from "providers/Auth/helpers";

const UPDATE = "UPDATE";
const RESET = "RESET";

const INITIAL_AUTH = {
  status: AuthStatus.INITIALIZING,
  isAuthenticated: false,

  uid: null,
  email: null,
  displayName: null,

  role: "tenant",
  claims: null,

  tenant: null,
  availableTenants: null,

  error: null,

  onboardingMode: ONBOARDING_STEPS.none,
};

function reducer(state, action) {
  switch (action.type) {
    case UPDATE:
      return { ...state, ...action.patch };
    case RESET:
      return INITIAL_AUTH;
    default:
      throw new Error("No matching type for Auth reducer");
  }
}

const Provider = ({ globalConfig, children }) => {
  const [auth, dispatch] = useReducer(reducer, INITIAL_AUTH);

  const claimsRequestId = useRef(0);

  const updateAuth = (patch) => dispatch({ type: UPDATE, patch });
  const resetAuth = () => dispatch({ type: RESET });

  const onboardingModeRef = useRef(ONBOARDING_STEPS.none);

  useEffect(() => {
    const persisted = sessionStorage.getItem("sigil:onboardingMode");
    if (persisted) {
      onboardingModeRef.current = persisted;
      dispatch({ type: UPDATE, patch: { onboardingMode: persisted } });
    }
  }, []);

  const hydrateFromFirebaseUser = useCallback(
    async (u, { forceRefresh = false } = {}) => {
      const reqId = ++claimsRequestId.current;
      const isFinalizing =
        onboardingModeRef.current === ONBOARDING_STEPS.finalizing;

      updateAuth({
        status: "loadingClaims",
        isAuthenticated: true,
        uid: u.uid,
        email: u.email,
        displayName: u.displayName,
        error: null,
      });

      try {
        const tokenResult = await u.getIdTokenResult(forceRefresh);

        // stale request -> ignore
        if (reqId !== claimsRequestId.current) return null;

        const claims = tokenResult?.claims || {};
        const roleFromClaims = claims.role || "tenant";

        const hasTenantClaim =
          typeof claims.tenant === "string" || Array.isArray(claims.tenant);

        if (!hasTenantClaim) {
          const next = {
            status: AuthStatus.LOADING_CLAIMS,
            isAuthenticated: true,
            uid: u.uid,
            email: u.email,
            displayName: u.displayName,
            claims,
            role: roleFromClaims,
            tenant: null,
            availableTenants: null,
            error: null,
          };
          updateAuth(next);
          return next;
        }

        const { selectedTenant, availableTenants } = resolveTenantFromClaims(
          claims.tenant,
          u.uid,
        );

        const needsTenant =
          (availableTenants?.length ?? 0) > 1 && !selectedTenant;

        const nextStatus = needsTenant
          ? AuthStatus.NEEDS_TENANT
          : AuthStatus.READY;

        // During finalizing, only suppress "needsTenant" redirects
        const safeStatus =
          isFinalizing && nextStatus === AuthStatus.NEEDS_TENANT
            ? AuthStatus.READY
            : nextStatus;

        const next = {
          status: safeStatus,
          isAuthenticated: true,
          uid: u.uid,
          email: u.email,
          displayName: u.displayName,
          claims,
          role: roleFromClaims,
          tenant: selectedTenant,
          availableTenants,
          error: null,
        };

        updateAuth(next);

        if (selectedTenant && !isFinalizing) {
          saveLastTenant(u.uid, selectedTenant);
        }

        return next;
      } catch (err) {
        console.error("Failed to hydrate claims:", err);
        if (reqId !== claimsRequestId.current) return null;

        const next = {
          status: AuthStatus.ERROR,
          isAuthenticated: true,
          uid: u.uid,
          email: u.email,
          displayName: u.displayName,
          claims: null,
          role: "tenant",
          tenant: null,
          availableTenants: null,
          error: "Failed to load claims",
        };

        updateAuth(next);
        return next;
      }
    },
    [],
  );

  useEffect(() => {
    const unsub = onIdTokenChanged(firebaseAuth, async (u) => {
      const mode = onboardingModeRef.current;

      if (!u) {
        if (mode === ONBOARDING_STEPS.finalizing) {
          updateAuth({
            status: AuthStatus.LOADING_CLAIMS,
            isAuthenticated: true,
            error: null,
          });
          return;
        }
        resetAuth();
        updateAuth({ status: AuthStatus.SIGNED_OUT });
        return;
      }

      await hydrateFromFirebaseUser(u, { forceRefresh: false });
    });

    return () => unsub();
  }, [hydrateFromFirebaseUser]);

  const refreshClaims = useCallback(
    async ({ forceRefresh = true } = {}) => {
      const u = firebaseAuth.currentUser;
      if (!u) {
        resetAuth();
        updateAuth({ status: AuthStatus.SIGNED_OUT });
        return;
      }
      return await hydrateFromFirebaseUser(u, { forceRefresh });
    },
    [hydrateFromFirebaseUser],
  );

  /**
   * Membership/role helpers remain mostly the same.
   * (These probably should not default to true long-term, but we keep behavior consistent.)
   */
  const hasValidMembership = useCallback(
    (requiredPermissionRank = 100) => {
      if (!globalConfig?.tenant) return true;
      const currentPlan = globalConfig.tenant?.props?.currentPlan;
      const { permissionRank } = membershipTypes[currentPlan] || {
        permissionRank: 999,
      };
      return permissionRank <= requiredPermissionRank;
    },
    [globalConfig],
  );

  const hasValidRole = useCallback(
    (requiredPermissionRank = 100) => {
      if (!globalConfig?.tenant) return true;
      const normalizedRole = auth.role?.toUpperCase() || "TENANT";
      const roleConfig = roles[normalizedRole];
      if (!roleConfig) return true;
      return roleConfig.permissionRank <= requiredPermissionRank;
    },
    [auth.role, globalConfig],
  );

  const needsTenantSelection = auth.status === AuthStatus.NEEDS_TENANT;

  const selectTenant = useCallback(
    (tenant) => {
      const u = firebaseAuth.currentUser;
      const uid = u?.uid || auth.uid;
      if (!uid) return;

      saveLastTenant(uid, tenant);

      updateAuth({
        tenant,
        availableTenants: null,
        status: AuthStatus.READY,
        error: null,
      });
    },
    [auth.uid],
  );

  const setOnboardingMode = useCallback((mode) => {
    onboardingModeRef.current = mode;
    sessionStorage.setItem("sigil:onboardingMode", mode);
    dispatch({ type: UPDATE, patch: { onboardingMode: mode } });
  }, []);

  return (
    <Context.Provider
      value={{
        auth,
        updateAuth,
        refreshClaims,
        hasValidMembership,
        hasValidRole,
        needsTenantSelection,
        selectTenant,
        setOnboardingMode,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default Provider;
